// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import { systemBuilderStyles } from "@plumberssupply/App/Widgets/SystemBuilder/SystemBuilder";
import IconLinkArrow from "@plumberssupply/XDL/Icons/IconLinkArrow";
import IconPaginationLeft from "@plumberssupply/XDL/Icons/IconPaginationLeft";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import { iconLinkCommonStyles, mergeWithCss } from "@plumberssupply/XDL/Utilities";
import { css } from "styled-components";

systemBuilderStyles.wrapper = {
    css: css`
        min-height: 530px;
        ${xScreen("lte", "md")} {
            min-height: unset;
            margin-bottom: 17px;
        }
    `,
};
systemBuilderStyles.title = {
    variant: "h1",
    css: css`
        margin-bottom: 0;
    `,
};

systemBuilderStyles.topSection = {
    css: css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 17px;
        border-bottom: 1px solid ${color("common.ui7")};
        margin-bottom: 12px;
        ${xScreen("lte", "md")} {
            flex-direction: column;
            row-gap: 16px;
            align-items: flex-start;
        }
    `,
};

systemBuilderStyles.subTitleWrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
        }
    `,
};
systemBuilderStyles.subTitle = {
    variant: "h2",
    css: css`
        margin-bottom: 16px;
    `,
};
systemBuilderStyles.clearSpecButton = {
    variant: "secondary",
    css: css`
        ${xScreen("lte", "sm")} {
            align-self: end;
            margin-bottom: 16px;
        }
    `,
};
systemBuilderStyles.filterTogglesWrapper = {
    css: css`
        display: flex;
        gap: 16px;
        margin-bottom: 16px;
    `,
};
systemBuilderStyles.previous = {
    ...mergeWithCss(iconLinkCommonStyles(IconLinkArrow), {
        icon: {
            iconProps: {
                src: IconLinkArrow,
                size: 13,
                color: "secondary",
                css: css`
                    margin-left: 0;
                    opacity: 0.5;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: 12,
            weight: 700,
            css: css`
                line-height: 14px;
            `,
        },
        css: css`
            margin-bottom: 12px;
            ${IconWrapper} {
                &,
                & svg {
                    width: 11px;
                    height: 13px;
                }
            }
        `,
    }),
};
systemBuilderStyles.stepperItem = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            position: relative;
            cursor: pointer;

            &[aria-disabled="true"] {
                cursor: no-drop;
            }

            ${({ isCompleted }: any) =>
                isCompleted &&
                css`
                    &:after {
                        background-color: ${color("common.ui2")};
                    }
                `}
            ${xScreen("lte", "xs")} {
                flex: 1 0 auto;
            }
        `,
    },
    circle: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 23px;
            height: 23px;
            border-radius: 50%;
            background-color: ${color("common.ui11", 0.5)};
            color: ${color("common.white")};
            margin-right: 8px;
            font-size: 12px;
            font-weight: 900;
            letter-spacing: 0;

            ${({ isCompleted, isActive }: any) =>
                (isCompleted || isActive) &&
                css`
                    background-color: ${color("primary")};
                    color: ${color("common.white")};
                `}

            ${xScreen("lte", "md")} {
                margin-right: 0;

                ${({ isActive }: any) =>
                    isActive &&
                    css`
                        margin-right: 8px;
                    `}
            }
        `,
    },
    circleIcon: {
        src: Check,
        size: 14,
        color: "common.white",
    },
    title: {
        variant: "h6",
        forwardAs: "p",
        css: css`
            white-space: nowrap;
            color: ${color("common.ui11", 0.5)};
            margin-bottom: 0;
            ${xScreen("lte", "md")} {
                ${({ isActive }: any) =>
                    !isActive &&
                    css`
                        display: none;
                    `}
            }

            ${({ isCompleted, isActive }: any) =>
                (isCompleted || isActive) &&
                css`
                    color: ${color("common.ui11")};
                `}
        `,
    },
};
systemBuilderStyles.stepperWrapper = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 52px;
        ${xScreen("lte", "md")} {
            column-gap: 24px;
        }

        & > li:last-child:after {
            display: none;
        }
        & > li:last-of-type {
            margin-right: 0;
        }

        height: 34px;
    `,
};
